/**
 * Classification queue — items whose category/altitude still need a human look.
 * Surfaces classification status separately from creation provenance (Doc 30).
 */

import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { RefreshCw, Search } from 'lucide-react';
import { getAppDefinition } from '../registry';
import { AppShell, BallLine, ScreenHeading } from '../components/AppShell';
import {
  AppCard,
  EmptyState,
  Eyebrow,
  SecondaryButton,
  StatusChip,
} from '../components/primitives';
import { CreationProvenanceBadge } from '../../components/prioritization/provenance/CreationProvenanceBadge';
import { useItemsStore } from '../../stores/prioritization/itemsStore';
import { getSafeSupabaseClient, getCurrentWorkspaceId } from '../../lib/supabaseSync';
import { getItemCountState } from '../../components/collection/types';
import { classificationQueue } from './classificationQueue';

type Filter = 'all' | 'ai' | 'empty' | 'proposed';

export default function ClassificationQueueScreen() {
  const app = getAppDefinition('classification')!;
  const navigate = useNavigate();
  const items = useItemsStore((s) => s.items);
  const fetchItems = useItemsStore((s) => s.fetchFromSupabase);
  const [filter, setFilter] = useState<Filter>('all');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [aiItemIds, setAiItemIds] = useState<Set<string>>(new Set());

  const reload = async () => {
    setLoading(true);
    setError(null);
    try {
      await fetchItems();
      const workspaceId = getCurrentWorkspaceId();
      const supabase = getSafeSupabaseClient();
      if (supabase && workspaceId) {
        const { data, error: fetchErr } = await supabase
          .from('item_classification_proposals')
          .select('item_id, proposed_by')
          .eq('workspace_id', workspaceId)
          .eq('status', 'active')
          .eq('proposed_by', 'agent');
        if (fetchErr) {
          setError(fetchErr.message);
        } else {
          setAiItemIds(
            new Set(
              (data ?? []).map((r) => String((r as { item_id: string }).item_id)).filter(Boolean),
            ),
          );
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load queue');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const queue = useMemo(() => classificationQueue(items), [items]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return queue.filter((item) => {
      const status = item.classificationStatus ?? 'empty';
      if (filter === 'empty' && status !== 'empty') return false;
      if (filter === 'proposed' && status !== 'proposed') return false;
      if (filter === 'ai' && !aiItemIds.has(item.id)) return false;
      if (q && !item.title.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [queue, filter, search, aiItemIds]);

  const countState = getItemCountState(filtered.length);
  const showSearch = getItemCountState(queue.length) === 'many' || search.length > 0;

  const filters: { id: Filter; label: string }[] = [
    { id: 'all', label: 'All' },
    { id: 'ai', label: 'AI proposals' },
    { id: 'proposed', label: 'Proposed' },
    { id: 'empty', label: 'Unclassified' },
  ];

  return (
    <AppShell
      app={app}
      actions={
        <SecondaryButton onClick={() => void reload()} disabled={loading} className="!px-3 !py-1.5">
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
          {loading ? 'Reading…' : 'Refresh'}
        </SecondaryButton>
      }
    >
      <ScreenHeading
        title="Is classification trustworthy?"
        subtitle="Confirm category and altitude — especially where AI proposed values."
      />
      <BallLine ballWith="you — accept, reject, or set classification" />

      <div className="mb-4 flex flex-wrap gap-2">
        {filters.map((f) => (
          <button
            key={f.id}
            type="button"
            onClick={() => setFilter(f.id)}
            className={`rounded-full px-3 py-1 font-mono text-[10px] uppercase tracking-wider transition-colors ${
              filter === f.id
                ? 'bg-primary-600 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-[var(--surface-raised)] dark:text-[var(--text-sec)]'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {showSearch && (
        <div className="relative mb-4">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search items…"
            className="w-full rounded-md border border-gray-300 bg-white py-2.5 pl-9 pr-3 font-body text-sm dark:border-[var(--rule)] dark:bg-[var(--surface-raised)] dark:text-[var(--text-pri)]"
          />
        </div>
      )}

      <AppCard>
        <Eyebrow>
          {filtered.length} of {queue.length} needing attention
        </Eyebrow>
        {error ? (
          <p className="mt-2 font-body text-sm text-luminous-coral-700 dark:text-luminous-coral-300">
            {error}
          </p>
        ) : loading ? (
          <p className="mt-2 font-body text-sm text-gray-500 dark:text-[var(--text-ter)]">
            Reading classification queue…
          </p>
        ) : countState === 'zero' ? (
          <div className="mt-3">
            <EmptyState
              title="Nothing waiting"
              hint="Every item in this workspace has an approved classification — or there are no items yet."
              action={
                <SecondaryButton onClick={() => navigate('/prioritization/items')}>
                  Open items
                </SecondaryButton>
              }
            />
          </div>
        ) : (
          <ul className="mt-3 divide-y divide-gray-100 dark:divide-[var(--rule)]">
            {filtered.map((item) => {
              const status = item.classificationStatus ?? 'empty';
              const isAi = aiItemIds.has(item.id);
              return (
                <li key={item.id}>
                  <Link
                    to={`/apps/classification/${item.id}`}
                    className="flex flex-col gap-2 py-3 hover:bg-gray-50 dark:hover:bg-[var(--hover-tint)] sm:flex-row sm:items-center sm:justify-between"
                  >
                    <div className="min-w-0">
                      <p className="truncate font-body text-sm font-medium text-gray-900 dark:text-[var(--text-pri)]">
                        {item.title}
                      </p>
                      <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                        <StatusChip
                          label={status === 'proposed' ? 'Proposed' : 'Unclassified'}
                          tone={status === 'proposed' ? 'mustard' : 'neutral'}
                        />
                        {isAi && <StatusChip label="AI proposal" tone="slate" />}
                        <CreationProvenanceBadge
                          source={item.creationSource}
                          reviewStatus={item.creationReviewStatus}
                          compact
                        />
                      </div>
                    </div>
                    <span className="shrink-0 font-mono text-[10px] uppercase tracking-wider text-gray-400">
                      Review →
                    </span>
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </AppCard>

      <p className="mt-4 font-body text-xs text-gray-500 dark:text-[var(--text-ter)]">
        Full report:{' '}
        <Link
          to="/prioritization/settings/classification-report"
          className="text-primary-600 hover:underline dark:text-primary-400"
        >
          Classification proposals
        </Link>
      </p>
    </AppShell>
  );
}
